import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { AppDispatch, RootState } from '@/features/store';
import { clearAuth, refreshToken } from '@/features/auth/authSlice';
import {
  markBackendReachable,
  markBackendUnavailable,
} from '@/services/connectivity';

// Lazy-load store to avoid circular dependency
let storeInstance: { getState: () => RootState; dispatch: AppDispatch } | null = null;

export const setStoreInstance = (store: { getState: () => RootState; dispatch: AppDispatch }) => {
  storeInstance = store;
};

const getStore = () => {
  if (!storeInstance) {
    throw new Error('Store not initialized. Call setStoreInstance first.');
  }
  return storeInstance;
};

/**
 * Get API base URL dynamically
 * - Uses VITE_API_URL if set
 * - Otherwise constructs URL from current location (works for localhost and network access)
 * - Falls back to localhost:5002 if all else fails
 */
const getApiBaseUrl = (): string => {
  // Priority 1: explicit env var (production builds, Cloud Run)
  if (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.trim() !== '') {
    return import.meta.env.VITE_API_URL;
  }
  // Priority 2: relative URL — routes through Vite dev proxy (/api → http://backend:3000)
  // The Vite proxy in vite.config.ts already handles /api → http://backend:3000.
  // This makes the request same-origin (no CORS) regardless of frontend hostname
  // (localhost, Tailscale, LAN — all work without backend CORS config changes).
  return '/api/v1';
};

// Create axios instance with default config
// NOTE: Do NOT set a default 'Content-Type' header here.
// Axios v1.x transformRequest converts FormData → JSON when Content-Type is
// 'application/json', causing multipart file uploads to silently lose the file.
// Axios automatically sets 'application/json' for plain objects and lets the
// browser set the correct 'multipart/form-data; boundary=...' for FormData.
const api = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true, // Enable sending cookies with requests
  timeout: 10000, // 10 seconds
});

// Request interceptor - Add auth token to requests (if using Bearer token)
// Note: With cookie-based auth, the browser automatically sends cookies
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    try {
      const store = getStore();
      const state = store.getState();
      const token = state.auth.token;
      
      // If we have a token in state (fallback for non-cookie scenarios), add it
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Store might not be initialized yet, continue without token
      console.debug('Store not initialized in request interceptor');
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors and token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

const getFailureStatus = (failure: unknown): number | undefined => {
  if (typeof failure !== 'object' || failure === null) return undefined;
  const directStatus = (failure as { status?: unknown }).status;
  if (typeof directStatus === 'number') return directStatus;
  const response = (failure as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) return undefined;
  const responseStatus = (response as { status?: unknown }).status;
  return typeof responseStatus === 'number' ? responseStatus : undefined;
};

const isExplicitAuthFailure = (failure: unknown): boolean => {
  const status = getFailureStatus(failure);
  return status === 401;
};

api.interceptors.response.use(
  (response) => {
    markBackendReachable();
    // Return successful responses as-is
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const responseStatus = error.response?.status;
    if (!error.response || (responseStatus !== undefined && responseStatus >= 500)) {
      markBackendUnavailable();
    } else {
      // A 4xx response proves the backend was reached.
      markBackendReachable();
    }
    
    // [T26P1] Auth endpoints must never trigger a refresh retry — doing so creates a
    // circular deadlock: /auth/me 401 → refresh → /auth/refresh 401 → interceptor
    // queues the refresh request → processQueue() never called → infinite hang.
    // Best practice: exclude all auth endpoints from the 401 retry/refresh logic.
    const requestUrl = originalRequest?.url || '';
    const isAuthEndpoint = ['/auth/refresh', '/auth/me', '/auth/login', '/auth/register',
      '/auth/otp/login/request', '/auth/otp/login/verify',
      '/auth/otp/register/request', '/auth/otp/register/verify',
    ].some(
      (path) => requestUrl.includes(path)
    );
    
    // A retried protected request has already exhausted refresh. Its 401 is
    // authoritative and must clear the cached profile.
    if (responseStatus === 401 && originalRequest?._retry) {
      getStore().dispatch(clearAuth());
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized errors (skip auth endpoints to prevent deadlock)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // [T24P0] Cookie-based refresh is attempted even when state.auth.token is null.
        // The non-secret profile snapshot restores the UI after reload; the server
        // still validates the httpOnly cookie on this first protected request.
        // Attempt to refresh the token via httpOnly cookies
        // The access token itself remains memory-only in production; cookies carry
        // the authenticated session across page reloads.
        const store = getStore();
        await store.dispatch(refreshToken()).unwrap();
        const newToken = store.getState().auth.token;
        
        processQueue(null, newToken);
        
        if (originalRequest.headers && newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        
        isRefreshing = false;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(error, null);
        isRefreshing = false;
        
        // [T45P0] An explicit refresh 401 clears auth; transport/server failures
        // preserve the cached profile so a Cloud Run cold start cannot force a
        // relogin. ProtectedRoute handles the redirect after a real invalidation.
        const store = getStore();
        if (isExplicitAuthFailure(refreshError)) {
          store.dispatch(clearAuth());
        }
        
        return Promise.reject(refreshError);
      }
    }
    
    // Handle 403 Forbidden errors
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data);
      // Could show a toast notification here
    }
    
    // Handle 404 Not Found errors
    if (error.response?.status === 404) {
      console.error('Resource not found:', error.config?.url);
    }
    
    // Handle 500+ Server errors
    if (error.response?.status && error.response.status >= 500) {
      console.error('Server error:', error.response.data);
      // Could show a toast notification here
    }
    
    // Handle network errors
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout');
      // Could show a toast notification here
    }
    
    if (error.message === 'Network Error') {
      console.error('Network error - please check your connection');
      // Could show a toast notification here
    }
    
    return Promise.reject(error);
  }
);

export default api;

// Utility function to handle API errors consistently
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // Server responded with error status
      return error.response.data?.message || error.response.data?.error || 'An error occurred';
    } else if (error.request) {
      // Request was made but no response received
      return 'No response from server. Please check your connection.';
    } else {
      // Error in request setup
      return error.message || 'Failed to make request';
    }
  }
  
  return 'An unexpected error occurred';
};

// Type-safe API response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

// API endpoint helpers
export const endpoints = {
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    logout: '/auth/logout',
    logoutAll: '/auth/logout-all',
    refresh: '/auth/refresh',
    me: '/auth/me',
    changePassword: '/auth/change-password',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    verifyEmail: '/auth/verify-email',
    resendVerification: '/auth/resend-verification',
    profile: '/auth/profile',
    deleteAccount: '/auth/account',
    deletePreview: '/auth/delete-preview',
    exportData: '/auth/export-data',
    sessions: '/auth/sessions',
    revokeSession: (sessionId: string) => `/auth/sessions/${sessionId}`,
    securityEvents: '/auth/security-events',
    googleAuth: '/auth/google',
    unlinkGoogle: '/auth/google/unlink',
    mfaSetup: '/auth/mfa/setup',
    mfaEnable: '/auth/mfa/enable',
    mfaVerify: '/auth/mfa/verify',
    mfaDisable: '/auth/mfa/disable',
    mfaStatus: '/auth/mfa/status',
    otpLoginRequest: '/auth/otp/login/request',
    otpLoginVerify: '/auth/otp/login/verify',
    otpRegisterRequest: '/auth/otp/register/request',
    otpRegisterVerify: '/auth/otp/register/verify',
  },
  accounts: {
    list: '/accounts',
    create: '/accounts',
    get: (id: string) => `/accounts/${id}`,
    update: (id: string) => `/accounts/${id}`,
    delete: (id: string) => `/accounts/${id}`,
    sync: (id: string) => `/accounts/${id}/sync`,
  },
  transactions: {
    list: '/transactions',
    create: '/transactions',
    get: (id: string) => `/transactions/${id}`,
    update: (id: string) => `/transactions/${id}`,
    delete: (id: string) => `/transactions/${id}`,
    bulkDelete: '/transactions/bulk-delete',
    export: '/transactions/export',
    summary: '/transactions/summary',
    transfer: '/transactions/transfer',
    recurring: '/transactions/recurring',
    processRecurring: '/transactions/process-recurring',
    duplicate: (id: string) => `/transactions/${id}/duplicate`,
    // [T83P2] Analytics endpoints for new insight tabs
    heatmap: '/transactions/analytics/heatmap',
    dayofweek: '/transactions/analytics/dayofweek',
    merchantSummary: '/transactions/analytics/merchant-summary',
    histogram: '/transactions/analytics/histogram',
    netCashflow: '/transactions/analytics/net-cashflow',
    // [T114-F4] Items analytics endpoints
    itemsSummary: '/transactions/analytics/items-summary',
    itemsFrequency: '/transactions/analytics/items-frequency',
    itemsSearch: '/transactions/analytics/items-search',
    // [T114-F5] Batch operations endpoints
    batchUpdate: '/transactions/batch/update',
    batchDelete: '/transactions/batch/delete',
    batchApplyRule: '/transactions/batch/apply-rule',
    batchTag: '/transactions/batch/tag',
    // [T155P1] Review Cockpit endpoints
    reviewQueue: '/transactions/review-queue',
    batchMarkReviewed: '/transactions/batch/mark-reviewed',
  },
  budgets: {
    list: '/budgets',
    create: '/budgets',
    current: '/budgets/current',
    get: (id: string) => `/budgets/${id}`,
    update: (id: string) => `/budgets/${id}`,
    delete: (id: string) => `/budgets/${id}`,
  },
  goals: {
    list: '/goals',
    create: '/goals',
    get: (id: string) => `/goals/${id}`,
    update: (id: string) => `/goals/${id}`,
    delete: (id: string) => `/goals/${id}`,
    contribute: (id: string) => `/goals/${id}/contribute`,
    complete: (id: string) => `/goals/${id}/complete`,
  },
  categories: {
    list: '/categories',
    create: '/categories',
    get: (id: string) => `/categories/${id}`,
    update: (id: string) => `/categories/${id}`,
    delete: (id: string) => `/categories/${id}`,
    // [T84P2] Category stats endpoint
    stats: (id: string) => `/categories/${id}/stats`,
  },
  data: {
    export: '/data/export',
    validateImport: '/data/validate-import',
    import: '/data/import',
  },
  analytics: {
    safeToSpend: '/analytics/safe-to-spend',
    runway: '/analytics/runway',
    netWorth: '/analytics/net-worth',
    snapshot: '/analytics/snapshot',
    subscriptions: '/analytics/detect-subscriptions',
    overview: '/analytics/overview',
    calendar: '/analytics/calendar-forecast',
    anomalies: '/analytics/anomalies',
    debtPayoff: '/analytics/debt-payoff',
    spendingTrends: '/analytics/spending-trends',
    taxSummary: '/analytics/tax-summary',
    budgetPerformance: '/analytics/budget-performance',
    spendingVelocity: '/analytics/spending-velocity',
    categoryTreemap: '/analytics/category-treemap',
    financialHealth: '/analytics/financial-health',
    expenseCalendar: '/analytics/expense-calendar',
    topMerchants: '/analytics/top-merchants',
    recurringVsDiscretionary: '/analytics/recurring-vs-discretionary',
    dayOfWeek: '/analytics/day-of-week',
    // [T155P2] Cash-Flow Foresight
    projectedBalance: '/analytics/projected-balance',
  },
  subscriptions: {
    list: '/subscriptions',
    create: '/subscriptions',
    get: (id: string) => `/subscriptions/${id}`,
    update: (id: string) => `/subscriptions/${id}`,
    delete: (id: string) => `/subscriptions/${id}`,
    pause: (id: string) => `/subscriptions/${id}/pause`,
    resume: (id: string) => `/subscriptions/${id}/resume`,
    cancel: (id: string) => `/subscriptions/${id}/cancel`,
    confirmPayment: (id: string) => `/subscriptions/${id}/confirm-payment`,
    previewTransaction: (id: string) => `/subscriptions/${id}/preview-transaction`,
    transactions: (id: string) => `/subscriptions/${id}/transactions`,
    summary: '/subscriptions/summary',
    calendar: '/subscriptions/calendar',
    processBillingConfirm: (id: string) => `/subscriptions/${id}/process-billing-confirm`,
  },
  transactionTemplates: {
    list: '/transaction-templates',
    create: '/transaction-templates',
    get: (id: string) => `/transaction-templates/${id}`,
    update: (id: string) => `/transaction-templates/${id}`,
    delete: (id: string) => `/transaction-templates/${id}`,
    apply: (id: string) => `/transaction-templates/${id}/apply`,
    fromTransaction: (txId: string) => `/transaction-templates/from-transaction/${txId}`,
  },
  bills: {
    list: '/bills',
    create: '/bills',
    upcoming: '/bills/upcoming',
    overdue: '/bills/overdue',
    summary: '/bills/summary',
    get: (id: string) => `/bills/${id}`,
    update: (id: string) => `/bills/${id}`,
    delete: (id: string) => `/bills/${id}`,
    pay: (id: string) => `/bills/${id}/pay`,
    generateNext: (id: string) => `/bills/${id}/next`,
  },
  transactionRules: {
    list: '/transaction-rules',
    create: '/transaction-rules',
    get: (id: string) => `/transaction-rules/${id}`,
    update: (id: string) => `/transaction-rules/${id}`,
    delete: (id: string) => `/transaction-rules/${id}`,
    test: (id: string) => `/transaction-rules/${id}/test`,
    apply: (id: string) => `/transaction-rules/${id}/apply`,
    applyAll: '/transaction-rules/apply',
    undo: (batchId: string) => `/transaction-rules/undo/${batchId}`,
  },
  notifications: {
    list: '/notifications',
    markRead: (id: string) => `/notifications/${id}/read`,
    markAllRead: '/notifications/read-all',
    delete: (id: string) => `/notifications/${id}`,
    clearRead: '/notifications/clear-read',
    unreadCount: '/notifications/unread-count',
  },
currency: {
rates: '/currency/rates',
convert: '/currency/convert',
supported: '/currency/supported',
events: '/currency/events',
event: (eventId: string) => `/currency/events/${eventId}`,
rollback: (eventId: string) => `/currency/events/${eventId}/rollback`,
},
  extensions: {
    list: '/extensions',
    get: (extensionId: string) => `/extensions/${extensionId}`,
    toggle: (extensionId: string) => `/extensions/${extensionId}/toggle`,
    settings: (extensionId: string) => `/extensions/${extensionId}/settings`,
    models: (extensionId: string) => `/extensions/${extensionId}/models`,
  },
  dashboards: {
    list: '/dashboards',
    create: '/dashboards',
    get: (id: string) => `/dashboards/${id}`,
    update: (id: string) => `/dashboards/${id}`,
    delete: (id: string) => `/dashboards/${id}`,
    clone: (id: string) => `/dashboards/${id}/clone`,
    data: (id: string) => `/dashboards/${id}/data`,
    layoutPresets: (id: string) => `/dashboards/${id}/layout-presets`,
    layoutPresetById: (id: string, presetId: string) => `/dashboards/${id}/layout-presets/${presetId}`,
    applyLayoutPreset: (id: string, presetId: string) => `/dashboards/${id}/layout-presets/${presetId}/apply`,
    layoutReset: (id: string) => `/dashboards/${id}/layout-reset`,
    descriptors: '/dashboards/descriptors',
    widgetCatalog: '/dashboards/widget-catalog',
    widgetCompatibilityCheck: (id: string) => `/dashboards/${id}/widgets/compatibility-check`,
    batchCompatibilityCheck: (id: string) => `/dashboards/${id}/widgets/batch-compatibility`,
    replaceWidgetKind: (id: string, widgetId: string) => `/dashboards/${id}/widgets/${widgetId}`,
    templates: '/dashboards/templates',
    fromTemplate: (templateId: string) => `/dashboards/from-template/${templateId}`,
  },
  audit: {
    history: (resource: string, id: string) => `/audit/${resource}/${id}/history`,
    rollback: (resource: string, id: string, snapshotId: string) =>
      `/audit/${resource}/${id}/rollback/${snapshotId}`,
  },
  timezones: {
    list: '/timezones',
  },
  receipts: {
    list: '/receipts',
    upload: '/receipts',
    get: (id: string) => `/receipts/${id}`,
    file: (id: string) => `/receipts/${id}/file`,
    thumbnail: (id: string) => `/receipts/${id}/thumbnail`,
    update: (id: string) => `/receipts/${id}`,
    delete: (id: string) => `/receipts/${id}`,
    link: (id: string) => `/receipts/${id}/link`,
    unlink: (id: string) => `/receipts/${id}/unlink`,
    categorize: (id: string) => `/receipts/${id}/categorize`,
    stats: '/receipts/stats',
    insightsStorage: '/receipts/insights/storage',
    insightsCategories: '/receipts/insights/categories',
    insightsDuplicates: '/receipts/insights/duplicates',
    shares: '/receipts/shares',
    shareById: (shareId: string) => `/receipts/shares/${shareId}`,
  },
  shared: {
    view: (token: string) => `/shared/${token}`,
    file: (token: string) => `/shared/${token}/file`,
  },
};
